import { AttachmentFile, FilesPreview, getFileThumbnail, Icon, SkeletonBox, Text, useModal } from '@billy/design-system'
import React, { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { useSegment } from '../../../../../../../../hooks'
import { getClassName, getImageClassName } from '../../../../../../../../utils/getClassName'
import * as Styled from './styles'

interface AttachmentTileImageProps {
  attachmentPreviewModalId: string
  attachmentImageFile: AttachmentFile
  onClick?: () => void
  thumbnailBadge?: ReactNode
}

export const AttachmentTileImage = ({
  attachmentPreviewModalId,
  attachmentImageFile,
  onClick,
  thumbnailBadge,
}: AttachmentTileImageProps): ReactElement => {
  const { track } = useSegment()
  const [isAttachmentImageLoading, setIsAttachmentImageLoading] = useState(true)
  const { t } = useTranslation()
  const { open: openAttachmentPreviewModal } = useModal(attachmentPreviewModalId)

  const attachmentImageFileName = attachmentImageFile.fileName || undefined
  const attachmentImageFiles = useMemo(
    () => [{ src: getFileThumbnail(attachmentImageFile, 'medium') }],
    [attachmentImageFile],
  )

  const handleImageLoad = useCallback(() => {
    setIsAttachmentImageLoading(false)
  }, [])

  const handleImageClick = useCallback(() => {
    if (onClick) {
      onClick?.()
    } else {
      track(ExpensesTrackingEvents.AttachmentPreviewButtonClick)
      openAttachmentPreviewModal()
    }
  }, [onClick, openAttachmentPreviewModal, track])

  return (
    <Styled.AttachmentTileImageWrapper onClick={handleImageClick} title={attachmentImageFileName}>
      <Styled.AttachmentTilePreviewWrapper loading={isAttachmentImageLoading}>
        <FilesPreview
          className={getImageClassName()}
          files={attachmentImageFiles}
          fitToWidth
          onLoad={handleImageLoad}
          withNavigation={false}
          fitType="cover"
        />
      </Styled.AttachmentTilePreviewWrapper>
      {thumbnailBadge && <Styled.AttachmentTileThumbnailBadge>{thumbnailBadge}</Styled.AttachmentTileThumbnailBadge>}
      <Styled.AttachmentHoverOverlay>
        <Text alignment="center" colorVariant="secondary" className={getClassName('label-deprecated')}>
          <Icon icon="eye" color="currentColor" /> {t('receipts.receipts_list.preview_attachment')}
        </Text>
      </Styled.AttachmentHoverOverlay>
      {isAttachmentImageLoading && <SkeletonBox fullHeight fullWidth />}
    </Styled.AttachmentTileImageWrapper>
  )
}
